/* [PATCH pomoc-fe v2] Vsebina pomoči — aplikacija Potni nalogi.
   Vloge: 'vsi' = vsi zaposleni; 'admin' = admin PN (stranka z vlogo admin) in računovodja — kot meni (pnVloga).
   Vir napisov: recon v10 (15.10.2026) iz PotniNalogiPage.tsx, PnPrenosPage.tsx, KadriPage.tsx na testu.
   Pravilo: samo napisi, ki obstajajo v kodi. Ob spremembi zaslona posodobi tudi to datoteko. */
import { Postopek, StranPomoci, Tezava } from '../tipi';

const P_NOV: Postopek = {
  id: 'pn.nov', naslov: 'Nov potni nalog', za: 'vsi',
  koraki: [
    'Meni **Potni nalogi** → **Nov potni nalog**.',
    'Izberi **Zaposleni** in **Namen poti** (vpiši ali izberi v šifrantu namenov poti).',
    'Vpiši **Odhod** in **Prihod**.',
    '**Relacija**: **Od** (izhodišče, sedež podjetja) in **Do** (končni kraj); vmesne kraje dodaš z **Dodaj postanek**; označi **Povratek na izhodišče**, če se vrneš.',
    '**Država (dnevnice)** določa dnevnice; po želji **Opomba**.',
    'Izberi **Vozilo** (ali **+ dodaj svoje vozilo …**: **Vozilo (znamka in model)**, **Registrska številka** → **Dodaj**).',
    '**Kilometri** se izračunajo iz relacije; lahko jih popraviš (ikona **Nazaj na izračun** vrne izračun). Po želji **Števec ob odhodu (km)** in **Števec ob prihodu (km)**.',
    'Pri dnevnicah označiš **Zagotovljeni brezplačni obroki**, če so bili obroki zagotovljeni.',
  ],
};

const P_STROSKI: Postopek = {
  id: 'pn.stroski', naslov: 'Stroški, akontacija in obračun', za: 'vsi',
  koraki: [
    '**Dodaj strošek**: **Vrsta**, **Datum**, **Znesek**, valuta, **Opis** (npr. parkirišče). Označi **Povrne se zaposlenemu**, če ga je plačal zaposleni → **Končano**.',
    'Priloge (npr. račune) odpreš z **Odpri v novem zavihku** ali odstraniš z **Odstrani prilogo**.',
    'Povzetek: **Dnevnice**, **Kilometrina**, **Drugi stroški**, **Skupaj**, **Akontacija**. Pri tuji valuti se uporabi tečaj BSI zadnjega dne poti (lahko vpišeš ročni tečaj).',
    'Ko je nalog dokončan, klikni **Zaključi obračun**.',
    'Ikona **Kako je izračunano** pokaže, kako je znesek izračunan.',
  ],
};

const P_DEJANJA: Postopek = {
  id: 'pn.dejanja', naslov: 'Tisk, e-pošta, kopija, preklic', za: 'vsi',
  koraki: [
    '**Natisni / PDF** — nalog za tisk ali prenos.',
    '**Pošlji po e-pošti**: **Prejemnik**, **Zadeva**, **Besedilo**, po želji **Kopija meni**.',
    'V seznamu meni **Dejanja**: **Kopiraj v nov nalog**, **Preštevilči …**, **Izbriši nalog**, **Prekliči nalog**.',
  ],
};

const P_SEZNAM: Postopek = {
  id: 'pn.seznam', naslov: 'Seznam potnih nalogov', za: 'vsi',
  koraki: [
    'Iskanje po številki, namenu ali zaposlenem; filtri **Vsi zaposleni**, **Vsa stanja**, odhod od–do; **Počisti**.',
    'Stolpci: **Številka**, **Zaposleni**, **Odhod**, **Namen**, **Vozilo**, **Država**, **Km**, **Povračilo** (dnevnice + kilometrina + stroški za povračilo − akontacija), **Stanje**.',
  ],
};

const P_NAMENI: Postopek = {
  id: 'pn.nameni', naslov: 'Nameni poti', za: 'vsi',
  koraki: [
    'Stran **Namen poti**: nov namen (npr. »servis pri stranki«) → **Dodaj**.',
    'Obstoječega urediš (**Uredi** → **Shrani**) ali odstraniš (**Odstrani**).',
    'Pri izbiri iz naloga klik na vrstico prenese namen na potni nalog (**Nazaj brez izbire** za preklic).',
  ],
};

const P_VOZILA: Postopek = {
  id: 'pn.vozila', naslov: 'Vozila', za: 'admin',
  koraki: [
    'Stran **Vozila** → **Dodaj vozilo**: **Vrsta** (**Službeno** / **Lastno (zaposlenega)**), **Vozilo (znamka in model)**, **Registrska številka**, **Lastnik (zaposleni)**, **Začetno stanje km** → **Shrani**.',
    'Seznam: **Vozilo**, **Registrska**, **Začetni km**, **Trenutno km**, **Lastnik**, **Stanje**; **Umakni** / **Ponovno aktiviraj**; **Prikaži neaktivna**.',
  ],
};

const P_ZAPOSLENI: Postopek = {
  id: 'pn.zaposleni', naslov: 'Zaposleni', za: 'admin',
  koraki: [
    '**Dodaj zaposlenega** ali **Dodaj sebe kot zaposlenega**; pri zaposlenem izbereš vozilo (**Lastno vozilo** / **Službeno vozilo**) in **Aktiven** → **Shrani**.',
    'Več naenkrat: **Uvoz iz Excela** → **Prenesi predlogo** → izpolni → **Predogled** → **Potrdi uvoz**.',
    'Seznam: **Šifra**, **Priimek in ime**, **Davčna**, **Vozilo**, **Aktiven**, **Uporabnik**; filter **Aktivni** / **Neaktivni** / **Vsi**.',
  ],
};

const P_PRENOS: Postopek = {
  id: 'pn.prenos', naslov: 'Prenos potnih nalogov (KN-21)', za: 'admin',
  koraki: [
    'Meni **Prenos** — **Prenos potnih nalogov**; zavihka **Neprenešeni** / **Preneseni**, razpon številk.',
    'Seznam: **Številka**, **Zaposleni**, **Prihod**, **Dnevnice**, **Kilometrina**, **Stroški (povrne)**, **Stroški (podjetje)**, **Akontacija**, **Preneseno**.',
    'Označi naloge → **Predogled knjižb** → **Prenesi (KN-21)**. Napačen prenos vrneš z **Označi neprenešeno**.',
    '**Nastavitve prenosa**: konti (**Konto**, **Opis**, **Breme**, **Dobro**), **Simbol v Vascu**, **Drug konto za posamezno vrsto stroška**; **Ponastavi na privzeto** / **Shrani**.',
  ],
};

const P_NASTAVITVE: Postopek = {
  id: 'pn.nastavitve', naslov: 'Nastavitve potnih nalogov', za: 'admin',
  koraki: [
    'Meni **NASTAVITVE → Nastavitve**.',
    'Vir zneskov dnevnic: **Davčni neobdavčeni zneski (uredba)**, **Kolektivna pogodba** ali **Interni pravilnik**.',
    'Številčenje: **Po zaposlenem — vsak zaposleni ima svoje zaporedje** ali **Skupno — eno zaporedje za vse zaposlene**.',
    '**Dovoli brisanje potnih nalogov**; naslov izhodišča (začni tipkati naslov) → **Shrani**.',
  ],
};

const T_IZRACUN: Tezava = { vprasanje: 'Nalog ne izračuna zneskov', odgovor: '**Vpiši odhod in prihod.** in **Izberi zaposlenega.** — brez tega izračuna ni.', za: 'vsi' };

export const POTNI_NALOGI: StranPomoci[] = [
  {
    kljuc: 'pn.seznam', naslov: 'Potni nalogi', za: 'vsi',
    ujema: (pot) => pot === '/potni-nalogi',
    opis: 'Seznam potnih nalogov. Nov nalog ustvariš z **Nov potni nalog**.',
    postopki: [P_NOV, P_SEZNAM, P_DEJANJA],
  },
  {
    kljuc: 'pn.nalog', naslov: 'Potni nalog', za: 'vsi',
    ujema: (pot) => pot.startsWith('/potni-nalogi/'),
    opis: 'Podatki poti, vozilo, stroški in obračun naloga.',
    postopki: [P_NOV, P_STROSKI, P_DEJANJA],
    tezave: [T_IZRACUN],
  },
  {
    kljuc: 'pn.sifranti', naslov: 'Šifranti', za: 'vsi',
    ujema: (pot) => pot === '/pn-sifranti' || pot === '/nameni-poti' || pot === '/vozila',
    opis: 'Šifranti potnih nalogov.',
    postopki: [P_NAMENI, P_ZAPOSLENI, P_VOZILA],
  },
  {
    kljuc: 'pn.prenos', naslov: 'Prenos potnih nalogov', za: 'admin',
    ujema: (pot) => pot === '/pn-prenos',
    opis: 'Prenos obračunanih potnih nalogov v Vasco (KN-21).',
    postopki: [P_PRENOS],
  },
  {
    kljuc: 'pn.nastavitve', naslov: 'Nastavitve', za: 'admin',
    ujema: (pot) => pot === '/kadri',
    opis: 'Nastavitve potnih nalogov in zaposleni.',
    postopki: [P_NASTAVITVE, P_ZAPOSLENI],
  },
];
